// ---------------------------------------------------------------------------
// SOC-004 — Unified Content Calendar (frontend)
// ---------------------------------------------------------------------------
// Single timeline view of every tenant-scoped scheduled content item across
// the five content surfaces (social / blog / email / event / review request).
//
// Backend contract: `GET /v1/content/calendar/unified?from=ISO&to=ISO&types=...`
// returns `{ data: { items[], from, to, types } }`. Each item is a discriminated
// union on `type`. See `workers/api-crm/src/routes/content-calendar-unified.ts`.
//
// CSS uses the Stero design tokens — no hardcoded brand colors; tokens map to
// electric-blue / power-purple / action-orange in `global.css`. No emojis.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, CalendarDays, Loader2, MessageSquare } from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { EmptyState } from "@/components/ui/empty-state";
import {
  CALENDAR_TYPE_VISUALS,
  UNIFIED_CALENDAR_TYPES,
  dateInputToIsoEnd,
  dateInputToIsoStart,
  defaultCalendarRange,
  describeCalendarItem,
  formatItemDate,
  type UnifiedCalendarItem,
  type UnifiedCalendarResponse,
  type UnifiedCalendarType,
} from "./calendar-types";

// Re-export for tests / future consumers that import from the route module.
export {
  UNIFIED_CALENDAR_TYPES,
  type UnifiedCalendarItem,
  type UnifiedCalendarType,
} from "./calendar-types";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CalendarUnifiedState {
  status: "idle" | "loading" | "ready" | "error";
  items: UnifiedCalendarItem[];
  error: string | null;
}

export default function CalendarUnified() {
  const initialRange = useMemo(defaultCalendarRange, []);
  const [fromDate, setFromDate] = useState<string>(initialRange.from);
  const [toDate, setToDate] = useState<string>(initialRange.to);
  const [activeTypes, setActiveTypes] = useState<UnifiedCalendarType[]>([
    ...UNIFIED_CALENDAR_TYPES,
  ]);
  const [state, setState] = useState<CalendarUnifiedState>({
    status: "idle",
    items: [],
    error: null,
  });

  const loadCalendar = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setState((prev) => ({ ...prev, status: "loading", error: null }));

    const params = new URLSearchParams();
    params.set("from", dateInputToIsoStart(fromDate));
    params.set("to", dateInputToIsoEnd(toDate));
    for (const type of activeTypes) {
      params.append("types", type);
    }

    try {
      const response = await apiFetch<UnifiedCalendarResponse>(
        `/v1/content/calendar/unified?${params.toString()}`,
      );
      setState({ status: "ready", items: response.data.items, error: null });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to load calendar";
      setState({ status: "error", items: [], error: message });
    }
  }, [fromDate, toDate, activeTypes]);

  useEffect(() => {
    void loadCalendar();
  }, [loadCalendar]);

  const toggleType = useCallback((type: UnifiedCalendarType) => {
    setActiveTypes((prev) => {
      const has = prev.includes(type);
      if (has) {
        const next = prev.filter((t) => t !== type);
        return next.length === 0 ? [...UNIFIED_CALENDAR_TYPES] : next;
      }
      return [...prev, type];
    });
  }, []);

  const visibleItems = useMemo(() => {
    const allowed = new Set(activeTypes);
    return state.items.filter((item) => allowed.has(item.type));
  }, [state.items, activeTypes]);

  return (
    <div className="space-y-6" data-testid="calendar-unified-root">
      <header>
        <h1 className="text-2xl font-bold text-[var(--color-foreground)]">
          Content calendar
        </h1>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          Every scheduled social post, blog, email, event, and review request — one timeline.
        </p>
      </header>

      <CalendarControls
        fromDate={fromDate}
        toDate={toDate}
        activeTypes={activeTypes}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onToggleType={toggleType}
      />

      {state.status === "error" ? (
        <CalendarErrorBanner error={state.error ?? ""} onRetry={loadCalendar} />
      ) : null}

      {state.status === "loading" ? (
        <div
          className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]"
          data-testid="calendar-unified-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading content…
        </div>
      ) : null}

      {state.status === "ready" && visibleItems.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="h-8 w-8" aria-hidden="true" />}
          title="Nothing scheduled in this range"
          description="Pick a wider date range, enable more content types, or schedule a new post to see it here."
          variant="info"
        />
      ) : null}

      {visibleItems.length > 0 ? (
        <ol
          className="space-y-2"
          data-testid="calendar-unified-items"
          aria-label="Scheduled content items"
        >
          {visibleItems.map((item) => (
            <CalendarItemRow key={`${item.type}:${item.id}`} item={item} />
          ))}
        </ol>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error banner
// ---------------------------------------------------------------------------

function CalendarErrorBanner({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div
      role="alert"
      data-testid="calendar-unified-error"
      className="flex items-start gap-3 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]"
    >
      <AlertCircle className="mt-0.5 h-4 w-4" aria-hidden="true" />
      <div className="flex-1">
        <p className="font-medium">Couldn&apos;t load calendar</p>
        <p className="mt-0.5 opacity-90">{error}</p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md border border-[var(--color-destructive)]/40 px-3 py-1 text-xs font-medium hover:bg-[var(--color-destructive)]/10"
      >
        Retry
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Controls — date range + type filter
// ---------------------------------------------------------------------------

interface CalendarControlsProps {
  fromDate: string;
  toDate: string;
  activeTypes: UnifiedCalendarType[];
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  onToggleType: (type: UnifiedCalendarType) => void;
}

function CalendarControls({
  fromDate,
  toDate,
  activeTypes,
  onFromChange,
  onToChange,
  onToggleType,
}: CalendarControlsProps) {
  return (
    <div className="flex flex-wrap items-end gap-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/60 p-4">
      <label className="flex flex-col gap-1 text-xs font-medium text-[var(--color-muted-foreground)]">
        From
        <input
          type="date"
          value={fromDate}
          max={toDate}
          onChange={(e) => onFromChange(e.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1.5 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
          data-testid="calendar-unified-from"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-[var(--color-muted-foreground)]">
        To
        <input
          type="date"
          value={toDate}
          min={fromDate}
          onChange={(e) => onToChange(e.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1.5 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
          data-testid="calendar-unified-to"
        />
      </label>
      <div
        className="flex flex-wrap items-center gap-2"
        role="group"
        aria-label="Filter by content type"
      >
        {UNIFIED_CALENDAR_TYPES.map((type) => {
          const visual = CALENDAR_TYPE_VISUALS[type];
          const Icon = visual.icon;
          const isActive = activeTypes.includes(type);
          return (
            <button
              key={type}
              type="button"
              aria-pressed={isActive}
              data-testid={`calendar-unified-filter-${type}`}
              onClick={() => onToggleType(type)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ${
                isActive
                  ? visual.badgeClass
                  : "border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
              }`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {visual.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Item row
// ---------------------------------------------------------------------------

function CalendarItemRow({ item }: { item: UnifiedCalendarItem }) {
  const visual = CALENDAR_TYPE_VISUALS[item.type];
  const Icon = visual.icon;
  const subtitle = describeCalendarItem(item);

  return (
    <li
      data-testid={`calendar-unified-item-${item.type}`}
      className="flex items-start gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 transition hover:border-[var(--color-primary)]/40"
    >
      <span
        className={`mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full ${visual.dotClass} text-white`}
        aria-hidden="true"
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${visual.badgeClass}`}
          >
            {visual.label}
          </span>
          <time
            className="text-xs text-[var(--color-muted-foreground)]"
            dateTime={item.date}
          >
            {formatItemDate(item.date)}
          </time>
          <span className="text-xs text-[var(--color-muted-foreground)]">
            • {item.status}
          </span>
        </div>
        <p className="mt-1 truncate text-sm font-medium text-[var(--color-foreground)]">
          {item.title}
        </p>
        {subtitle ? (
          <p className="mt-0.5 truncate text-xs text-[var(--color-muted-foreground)]">
            <MessageSquare
              className="-mt-0.5 mr-1 inline h-3 w-3"
              aria-hidden="true"
            />
            {subtitle}
          </p>
        ) : null}
      </div>
    </li>
  );
}
